import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DuplicateKeys, FieldSchemas, type DeviceClassRule } from '@regigov/contracts';
import { rulesApi } from '../../api/devices';
import { useAuth } from '../../auth/AuthContext';
import { isCerAdminRole } from '../../auth/roles';
import { Field, ProblemAlert, formatDateTime } from '../../components/ui';

const EMPTY_SCHEMA = { type: 'object', additionalProperties: false, properties: {}, required: [] };

function parseJson(text: string): { value?: unknown; error?: string } {
  try {
    return { value: JSON.parse(text) };
  } catch (e) {
    return { error: (e as Error).message };
  }
}

/** CER admin view: every class and version, plus "new version" that clones the current rule set into an editor. */
export function RulesPage() {
  const { user } = useAuth();
  const canWrite = isCerAdminRole(user?.roles);
  const rules = useQuery({ queryKey: ['rules', 'all'], queryFn: rulesApi.list });
  const [editing, setEditing] = useState<DeviceClassRule | 'new' | null>(null);

  const byClass = new Map<string, DeviceClassRule[]>();
  for (const r of rules.data?.items ?? []) byClass.set(r.deviceClass, [...(byClass.get(r.deviceClass) ?? []), r]);

  return (
    <>
      <p className="crumbs">
        <Link to="/cer/register">Device register</Link> / Rule sets
      </p>
      <div className="row">
        <h1>Device class rule sets</h1>
        {canWrite && (
          <button type="button" onClick={() => setEditing('new')}>
            New device class
          </button>
        )}
      </div>
      <p className="notice">
        Mandatory fields, allowed values and duplicate keys per device class are data, not code. Each version applies from its start date; registrations are validated against the
        version in force on their effective date, and every record keeps the version applied.
      </p>
      <ProblemAlert error={rules.error} />
      {editing && (
        <RuleEditor
          base={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      )}
      {[...byClass.entries()].map(([cls, versions]) => {
        const sorted = [...versions].sort((a, b) => b.version - a.version);
        const current = sorted.find((r) => !r.validTo) ?? sorted[0];
        if (!current) return null;
        return (
          <section className="card" key={cls}>
            <div className="row">
              <h2>
                {current.displayName} <code>{cls}</code>
              </h2>
              {canWrite && (
                <button type="button" className="linklike" onClick={() => setEditing(current)}>
                  New version from v{current.version}
                </button>
              )}
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>In force</th>
                  <th>Published</th>
                  <th>Reason</th>
                  <th>Definition</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id}>
                    <td>v{r.version}</td>
                    <td>
                      {r.validFrom} → {r.validTo ?? <em>open</em>}
                    </td>
                    <td>
                      {formatDateTime(r.createdAt)} <span className="muted">{r.createdByName ?? 'seed'}</span>
                    </td>
                    <td>{r.changeReason}</td>
                    <td>
                      <details>
                        <summary className="muted">schema &amp; duplicate keys</summary>
                        <pre>{JSON.stringify({ fieldSchema: r.fieldSchema, duplicateKeys: r.duplicateKeys }, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </>
  );
}

function RuleEditor({ base, onDone }: { base: DeviceClassRule | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [deviceClass, setDeviceClass] = useState(base?.deviceClass ?? '');
  const [displayName, setDisplayName] = useState(base?.displayName ?? '');
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [changeReason, setChangeReason] = useState('');
  const [schemaText, setSchemaText] = useState(JSON.stringify(base?.fieldSchema ?? { model: EMPTY_SCHEMA, unit: EMPTY_SCHEMA, installation: EMPTY_SCHEMA }, null, 2));
  const [dupText, setDupText] = useState(JSON.stringify(base?.duplicateKeys ?? { model: [], unit: [], installation: [] }, null, 2));
  const schema = parseJson(schemaText);
  const dup = parseJson(dupText);
  const fieldSchema = schema.error ? null : FieldSchemas.safeParse(schema.value);
  const duplicateKeys = dup.error ? null : DuplicateKeys.safeParse(dup.value);
  const schemaError = schema.error ?? (fieldSchema && !fieldSchema.success ? fieldSchema.error.issues[0]?.message : undefined);
  const dupError = dup.error ?? (duplicateKeys && !duplicateKeys.success ? duplicateKeys.error.issues[0]?.message : undefined);
  const publish = useMutation({
    mutationFn: () => {
      if (!fieldSchema?.success || !duplicateKeys?.success) throw new Error('Fix the JSON before publishing');
      return rulesApi.publish({ deviceClass, displayName, validFrom, fieldSchema: fieldSchema.data, duplicateKeys: duplicateKeys.data, changeReason });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!schemaError && !dupError) publish.mutate();
  };
  return (
    <section className="card">
      <h2>{base ? `New ${base.displayName} version (from v${base.version})` : 'New device class'}</h2>
      <form className="form" onSubmit={submit}>
        <div className="grid2">
          <Field label="Device class" hint="lower-case identifier, e.g. ev_charger">
            <input value={deviceClass} onChange={(e) => setDeviceClass(e.target.value)} readOnly={!!base} required pattern="[a-z][a-z0-9_]{1,39}" />
          </Field>
          <Field label="Display name">
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required minLength={2} />
          </Field>
          <Field label="Valid from" hint={base ? `Must be after v${base.version}'s start (${base.validFrom}); v${base.version} closes on this date` : 'First version start date'}>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
          </Field>
          <Field label="Change reason">
            <input value={changeReason} onChange={(e) => setChangeReason(e.target.value)} required minLength={3} />
          </Field>
        </div>
        <Field label="Field schemas (JSON Schema per record type: model, unit, installation)" hint={schemaError}>
          <textarea rows={16} value={schemaText} onChange={(e) => setSchemaText(e.target.value)} spellCheck={false} />
        </Field>
        <Field label="Duplicate keys (attribute combinations that must be unique among active records)" hint={dupError ?? 'e.g. {"installation": [["nmi"]]}'}>
          <textarea rows={5} value={dupText} onChange={(e) => setDupText(e.target.value)} spellCheck={false} />
        </Field>
        <ProblemAlert error={publish.error} />
        <div className="row">
          <button type="submit" disabled={publish.isPending || !!schemaError || !!dupError}>
            Publish version
          </button>
          <button type="button" className="linklike" onClick={onDone}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
